import { Trait } from 'api/query/shared/trait';
import colours from 'styles/_colours.module.scss';
import { isNullOrUndefined } from 'utils/utils';

export interface animalPercentiles {
  id?: string;
  barSeries: Array<any>;
  markerSeries: Array<any>;
}

const {
  white, purple40, lightBlue40, lightGreen40, grey40,
} = colours;

/**
 * Setting for trait chart.
 * @param chartId chart id
 * @param chartCategories categories to display
 * @param yAxisLevels amount of levels to show
 * @param customTooltip function that creates tooltip
 * @param chartWidth width of chart returned by chartRef
 */
export const chartOptions = (
  chartId: string,
  chartCategories: Array<string>,
  yAxisLevels: number,
  customTooltip: any,
  chartWidth: number | undefined,
  seriesQuantity?: number | undefined,
) => {
  let barWidth;
  if (chartWidth) {
    const barSpace = chartWidth / chartCategories.length;
    // calculates column width in % because apex chart accepts column width only in %,
    // 16 stands for 16px of width that column should have after chart render
    barWidth = (16 * 100) / barSpace;
  }

  const chartStateOptions: any = {
    chart: {
      toolbar: {
        show: false,
      },
      id: chartId,
      zoom: {
        enabled: false,
      },
    },
    // responsible for lines between markers and columns' border
    stroke: {
      width: 0,
    },
    // size of markers for each series in order
    markers: {
      size: [8],
      // colors must be passed to ApexCharts as an array, or the chart throws an uncaught exception when drawing >7 series
      colors: [white],
      showNullDataPoints: false,
      hover: {
        size: 8,
      },
    },
    legend: {
      show: false,
    },
    xaxis: {
      categories: chartCategories,
      tooltip: {
        enabled: false,
      },
      labels: {
        maxHeight: 0, // Huge a 0 max height instead of show: false to prevent rendering issues with the width
      },
      axisTicks: {
        borderType: 'solid',
        show: false,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: yAxisLevels,
      labels: {
        show: false,
      },
    },
    tooltip: {
      enabled: true,
      custom: (info: any) => customTooltip(info),
      enabledOnSeries: isNullOrUndefined(seriesQuantity) // If value not provided...
        ? [0] // enable on the first series
        : [...Array(seriesQuantity).keys()].map((n) => n * 2), // else, enable on every second series
      shared: false,
      intersect: true,
      theme: false,
      x: {
        show: false,
      },
      y: {
        show: false,
      },
    },
    grid: {
      show: true,
      position: 'back',
      borderColor: grey40,
      strokeDashArray: 2,
      column: {
        opacity: 1,
      },
      row: {
        opacity: 1,
      },
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0,
        shade: 'light',
        type: 'vertical',
        opacityTo: 0,
        stops: [0, 100],
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    plotOptions: {
      // styles for columns
      bar: {
        columnWidth: barWidth ? `${Math.round(barWidth)}%` : '14%',
        endingShape: 'rounded',
        colors: {
          ranges: [
            {
              from: 0,
              to: 80,
              color: grey40,
            },
            {
              from: 80,
              to: 90,
              color: lightGreen40,
            },
            {
              from: 90,
              to: 95,
              color: lightBlue40,
            },
            {
              from: 95,
              to: 100,
              color: purple40,
            },
          ],
        },
      },
    },
  };

  return chartStateOptions;
};

/**
   * Flip percentile from 0 to 100, where 100 is the highest score and 0 the lowest.
   * Makes it able to display on the chart.
   * @param percentile percentile
   */
export const flipPercentile = (percentile: number) => (percentile === null ? percentile : 100 - percentile);

/**
   * Creates two series of percentile values depend on asbv type.
   * @param chartASBVs animal asbvs
   */
export const getPercentiles = (chartASBVs: Trait[]) => {
  const s: animalPercentiles = {
    barSeries: [],
    markerSeries: [],
  };

  chartASBVs.forEach((asbv: Trait) => {
    // checks if animal data are accurate, if not sets series to null
    if (asbv.accuracyType === 0 && asbv.hasAccuracy) {
      // sets series for leader asbv or rbv and optimum traits
      s.barSeries.push(asbv.hasLeader ? asbv.percentile : null);
      s.markerSeries.push(asbv.percentile);
    } else {
      s.barSeries.push(null);
      s.markerSeries.push(null);
    }
  });

  return s;
};

/**
   * Assign appropriate colour depend on percentile score.
   */
export const getColour = (baseValue: any) => {
  let colour;
  const value = flipPercentile(baseValue);

  if (value < 80) {
    colour = colours.grey;
  } else if (value < 90) {
    colour = colours.midGreen;
  } else if (value < 95) {
    colour = colours.midBlue;
  } else {
    colour = colours.purple;
  }
  return colour;
};
